import {
  DropdownList,
  type DropdownListGroup,
  type DropdownListOptions,
} from "@/components/ui/dropdown-list/dropdown-list";
import type { IDropdownListItem } from "@/components/ui/dropdown-list/dropdown-list-item";
import {
  Button,
  GroupTextField,
  Heading,
  Icon,
  Input,
  TextField,
} from "@elaniintc/goes-design-system";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import SwitchField from "../form/components/SwitchField";
import { timerBlockSchema } from "@/schemas/expiration";
import type { TimerUnit, TimerBlockValues, TimeValuesConfig } from "./types";

type TimerBlockProps = {
  title: string;
  showLocalPreventions?: boolean;
  initialValue?: TimeValuesConfig;
  onChange: (values: TimerBlockValues) => void;
};

// Default options for the dropdown
const defaultOptions: DropdownListOptions = [
  {
    groupId: "unico-grupo",
    groupLabels: ["Nombre del nodo", "Paso"],
    icon: {
      type: "far",
      name: "bars",
      size: "xs",
      className: "text-action-quiet",
    },
    items: [
      {
        label: "inicio de formulario",
        detail: "str",
        value: "string",
        icon: {
          type: "far",
          name: "calendar",
        },
      },
    ],
  },
];

// --- Segundo select (sí devuelve valor) ---
const optionsLimit: IDropdownListItem[] = [
  { label: "Horas", value: "hour" },
  { label: "Días", value: "day" },
  { label: "Semanas", value: "week" },
  { label: "Meses", value: "month" },
];

export const TimerBlock = memo(
  ({
    title,
    initialValue,
    showLocalPreventions,
    onChange,
  }: TimerBlockProps) => {
    // Setup React Hook Form with schema validation
    const {
      control,
      watch,
      setValue,
      reset,
      formState: { isValid, errors },
    } = useForm({
      resolver: zodResolver(timerBlockSchema),
      defaultValues: {
        limit_unit: initialValue?.limit_unit,
        limit_amount: initialValue?.limit_amount,
        limit_preventions: initialValue?.limit_preventions,
        global_preventions: initialValue?.global_preventions ?? false,
        hasTimer: !!initialValue,
      },
      mode: "onChange",
    });

    // Watch form values to react to changes
    const formValues = watch();

    // Dropdown state (UI only)
    const defaultItem = defaultOptions[0].items[0];
    const defaultGroup = defaultOptions[0];
    const [selectedValue] = useState<IDropdownListItem>(defaultItem);
    const [selectedGroup] = useState<DropdownListGroup | undefined>(
      defaultGroup,
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredGroups, setFilteredGroups] =
      useState<DropdownListOptions>(defaultOptions);

    // Compute visibility based on form values
    const isVisible = useMemo(() => {
      return formValues.hasTimer;
    }, [formValues.hasTimer]);

    // Get current unit item for dropdown based on form value
    const currentUnitItem = useMemo(() => {
      if (!formValues.limit_unit) {
        return undefined;
      }
      return optionsLimit.find((o) => o.value === formValues.limit_unit);
    }, [formValues.limit_unit]);

    // Search functionality
    useEffect(() => {
      if (searchQuery) {
        const lowerQuery = searchQuery.toLowerCase();
        const newFilteredGroups = defaultOptions
          .map((group) => ({
            ...group,
            items: group.items.filter(
              (item) =>
                (item.label as string).toLowerCase().includes(lowerQuery) ||
                item.detail?.toLowerCase().includes(lowerQuery) ||
                false ||
                item.value.toLowerCase().includes(lowerQuery),
            ),
          }))
          .filter((group) => group.items.length > 0);

        setFilteredGroups(newFilteredGroups);
      } else {
        setFilteredGroups(defaultOptions);
      }
    }, [searchQuery]);

    // Update form values when initialValue changes
    useEffect(() => {
      if (!initialValue) return;
      reset({
        limit_unit: initialValue.limit_unit,
        limit_amount: initialValue.limit_amount,
        limit_preventions: !initialValue.global_preventions
          ? initialValue.limit_preventions
          : undefined,
        global_preventions: initialValue.global_preventions ?? false,
        hasTimer: !!initialValue,
      });
    }, [initialValue, setValue, reset]);

    // update local preventions when global is toggled
    useEffect(() => {
      if (formValues.global_preventions) {
        setValue("limit_preventions", undefined);
      }
    }, [formValues.global_preventions, setValue]);

    // Event handlers - memoized to prevent unnecessary recreation
    const handleAdd = useCallback(() => {
      reset({
        hasTimer: true,
        limit_unit: "day",
      });
    }, [reset]);

    const handleRemove = useCallback(() => {
      // Reset form to default values
      reset({
        hasTimer: false,
        limit_unit: "day",
      });

      // Reset UI state
      setSearchQuery("");
      setFilteredGroups(defaultOptions);
    }, [reset]);

    const handleSearchChange = useCallback((q: string) => {
      setSearchQuery(q);
    }, []);

    // Handler for form field changes
    const handleLimitUnitChange = useCallback(
      (item: IDropdownListItem) => {
        setValue("limit_unit", item.value as TimerUnit);
      },
      [setValue],
    );

    // Update parent component with all values from form state
    useEffect(() => {
      // Skip the initial render - only respond to changes
      if (!isVisible) {
        onChange({
          hasTimer: false,
          isComplete: false,
        });
        return;
      }

      onChange({
        hasTimer: formValues.hasTimer,
        isComplete: isValid,
        values: {
          limit_unit: formValues.limit_unit,
          limit_amount: formValues.limit_amount,
          limit_preventions: formValues.limit_preventions ?? undefined,
          global_preventions: formValues.global_preventions,
        },
      });
    }, [formValues, isVisible, onChange, isValid]);

    return (
      <div className="py-6 px-4 border-b border-neutral-quiet">
        {!isVisible && (
          <div>
            <Button variant="tertiary" onClick={handleAdd}>
              {title}
              <Icon type="far" name="plus" />
            </Button>
          </div>
        )}

        {isVisible && (
          <div className="flex flex-col gap-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Heading variant="h6" color="loud">
                  {title}
                </Heading>
              </div>
              <Button
                variant="danger-tertiary"
                size="xs"
                onClick={handleRemove}
              >
                Eliminar
                <Icon type="far" name="times" />
              </Button>
            </div>

            <div className="flex cursor-not-allowed">
              <GroupTextField
                label="Comenzar a partir de"
                required
                className="flex-1"
              >
                <DropdownList
                  disabled
                  widthClassName="flex-1"
                  placeholder="Elegir variable..."
                  options={filteredGroups}
                  selectedValue={selectedValue}
                  selectedGroup={selectedGroup}
                  toggleSelection={() => {}}
                  search={{
                    placeholder: "Buscar...",
                    query: searchQuery,
                    onSearch: handleSearchChange,
                  }}
                />
              </GroupTextField>
            </div>
            <GroupTextField
              label="Finalizar al transcurrir"
              required
              error={errors.limit_amount?.message || errors.limit_unit?.message}
            >
              <Controller
                name="limit_amount"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder="Escribe un número"
                    type="text"
                    group="left"
                    value={field.value === null ? "" : field.value}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      if (Number.isNaN(next) || e.target.value === "") {
                        field.onChange(null);
                      } else {
                        field.onChange(next);
                      }
                    }}
                    maxLength={15}
                    className="w-full py-[10.5px] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                )}
              />
              <DropdownList
                widthClassName="w-32"
                heightClassName="h-44"
                placeholder="Unidad"
                group="right"
                options={optionsLimit}
                selectedValue={currentUnitItem}
                toggleSelection={handleLimitUnitChange}
              />
            </GroupTextField>
            {showLocalPreventions && (
              <Controller
                name="global_preventions"
                control={control}
                render={({ field }) => (
                  <SwitchField
                    label="Consumir prórrogas globales"
                    checked={field.value || false}
                    onChange={field.onChange}
                    error={errors.global_preventions?.message}
                  />
                )}
              />
            )}
            {!formValues.global_preventions && (
              <Controller
                name="limit_preventions"
                control={control}
                render={({ field }) => (
                  <TextField
                    required
                    label="Prórrogas locales permitidas"
                    type="text"
                    placeholder="Escribir un número"
                    helperText=""
                    value={field.value === null ? "" : field.value}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      if (Number.isNaN(next) || e.target.value === "") {
                        field.onChange(undefined);
                      } else {
                        field.onChange(next);
                      }
                    }}
                    maxLength={15}
                    error={errors.limit_preventions?.message}
                    className="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                )}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);
